import { useEffect, useState } from 'react';
import BZAPIService from '../services/BZAPIService';
import SETTINGS from '../constants/settings';

// Aggregates the cross-product numbers shown on the BugZero Platform home.
// Every field is sourced from a real API; calls run in parallel and are
// individually resilient — a single failed call leaves its field null
// (the UI renders "—") rather than breaking the whole panel.
//
// Fields with no backend (e.g. an activity/audit feed, audit retention)
// are intentionally absent here — the UI shows an explicit empty state.
export default function usePlatformSummary() {
  const [summary, setSummary] = useState({
    loading: true,
    tenant: null,
    userEmail: null,
    preventFindings: null,
    planProjects: null,
    planInReview: null,
    memberCount: null,
    memberPending: null,
    ssoEnabled: null,
    integrationsConnected: null,
    integrationsTotal: null,
    freshness: null
  });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const results = await Promise.allSettled([
        BZAPIService.getMyUser(), // 0
        BZAPIService.getAccountInfo(), // 1
        BZAPIService.getDashboard(), // 2
        BZAPIService.bsaListProjects(), // 3
        BZAPIService.listUsers(), // 4
        BZAPIService.getSSOStatus(), // 5
        BZAPIService.getSetting(SETTINGS.SERVICE_NOW), // 6
        BZAPIService.getIntegrationSplunkStatus(), // 7
        BZAPIService.getKeys() // 8
      ]);

      if (cancelled) return;

      const val = i => (results[i].status === 'fulfilled' ? results[i].value : null);
      const me = val(0);
      const account = val(1);
      const dash = val(2);
      const projects = val(3);
      const users = val(4);
      const sso = val(5);
      const snSetting = val(6);
      const splunk = val(7);
      const keys = val(8);

      // Prevent — total open findings in the rolling 7-day window
      const preventFindings = Array.isArray(dash?.newBugsLastWeek)
        ? dash.newBugsLastWeek.reduce((s, d) => s + (Number(d.priorityCount) || 0), 0)
        : null;

      // Plan — project counts
      const planProjects = Array.isArray(projects) ? projects.length : null;
      const planInReview = Array.isArray(projects)
        ? projects.filter(p => (p.versions || []).some(v => v.state === 'In review')).length
        : null;

      // Members — total + pending (Cognito users not yet confirmed)
      const memberCount = Array.isArray(users) ? users.length : null;
      const memberPending = Array.isArray(users)
        ? users.filter(u => u.UserStatus && !['CONFIRMED', 'EXTERNAL_PROVIDER'].includes(u.UserStatus)).length
        : null;

      // SSO
      const ssoEnabled = sso ? !!sso.enabled : null;

      // Integrations — count what's actually connected out of the four the
      // platform tracks (ServiceNow, Splunk, BugZero API, SSO).
      const intChecks = [
        !!(snSetting && Object.keys(snSetting).length), // ServiceNow configured
        !!(splunk && (splunk.enabled || splunk.configured)), // Splunk on
        !!(Array.isArray(keys) && keys.length), // API key(s) issued
        !!ssoEnabled // SSO enforced
      ];

      setSummary({
        loading: false,
        tenant: account?.tenantId || me?.tenantId || null,
        userEmail: me?.email || null,
        preventFindings,
        planProjects,
        planInReview,
        memberCount,
        memberPending,
        ssoEnabled,
        integrationsConnected: intChecks.filter(Boolean).length,
        integrationsTotal: intChecks.length,
        freshness: new Date()
      });
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return summary;
}
